/*
 * Pure Shenandoah -- fade in/out motion controllers.
 *
 *   <pure-mega-motion>  the Shop mega menu panel (see below)
 *   <pure-tab-motion>   ARIA tab panels driven by pure-tabs.js -- used by the
 *                       homepage education tabs
 *
 * Both are section-scoped custom elements: they are created and torn down with
 * their section by the Theme Editor, guard their own define, and fall back to an
 * instant swap under prefers-reduced-motion.
 *
 * ---------------------------------------------------------------------------
 * <pure-mega-motion> -- fade in/out for the Shop mega menu panel.
 *
 * The Webflow source fades the dropdown in and out (IX2, data-duration="400",
 * ease). Dawn snaps it: `.mega-menu[open] .mega-menu__content` flips opacity
 * with no transition, and on close the <details> collapses instantly so there
 * is nothing left to animate.
 *
 * Dawn closes this panel from three places -- the native <summary> toggle,
 * HeaderMenu.close() on focusout, and onKeyUpEscape() in global.js -- and each
 * one just drops the `open` attribute. Rather than patch all three, this
 * controller watches `open` itself: when something removes it, we put it back,
 * run the fade, and only then close for real. One intercept covers every path,
 * including any future one.
 *
 * Section-scoped and idempotent: the element lives inside the panel, so it is
 * created and torn down with the header section by the Theme Editor.
 */
(function () {
  var OPEN_MS = 400;
  var CLOSE_MS = 300;

  /* Tab panels: fade the outgoing panel out, then the incoming one in.
     These defaults come from Webflow's tab components; an individual controller
     can override either duration with data-duration-out / data-duration-in:
       .spark-tabs-parent-6 (education) and .timeline15_tabs (process timeline)
       data-easing="ease" data-duration-in="300" data-duration-out="100"
     The asymmetry is the point: the old panel leaves quickly, the new one takes
     its time arriving. Any stylesheet using these classes must match. */
  var TAB_OUT_MS = 100;
  var TAB_IN_MS = 300;

  function durationFromAttribute(value, fallback) {
    var duration = Number.parseInt(value, 10);
    return Number.isFinite(duration) && duration >= 0 ? duration : fallback;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class PureMegaMotion extends HTMLElement {
    connectedCallback() {
      this.details = this.closest('details');
      this.content = this.closest('.mega-menu__content');
      if (!this.details || !this.content) return;

      this.closing = false;
      this.reopening = false;
      this.finishing = false;

      this.observer = new MutationObserver(this.onOpenChanged.bind(this));
      this.observer.observe(this.details, { attributes: true, attributeFilter: ['open'] });

      if (this.details.open) this.content.classList.add('is-open');
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.timer);
      if (this.content) this.content.classList.remove('is-open', 'is-closing');
      if (this.details) this.details.classList.remove('is-closing');
    }

    onOpenChanged() {
      if (this.details.open) {
        // Our own re-add during a fade-out -- not a user action.
        if (this.reopening) {
          this.reopening = false;
          return;
        }
        if (this.closing) {
          this.cancelClose();
          return;
        }
        this.content.classList.remove('is-closing');
        this.details.classList.remove('is-closing');
        if (prefersReducedMotion()) {
          this.content.classList.add('is-open');
          return;
        }
        // Two frames: the first paints the panel at opacity 0 so the second
        // has a value to transition from.
        requestAnimationFrame(
          function () {
            requestAnimationFrame(
              function () {
                if (this.details.open && !this.closing) this.content.classList.add('is-open');
              }.bind(this)
            );
          }.bind(this)
        );
        return;
      }

      // `open` was just removed by Dawn (or the native toggle).
      if (this.finishing) {
        this.finishing = false;
        return;
      }
      if (prefersReducedMotion()) {
        this.content.classList.remove('is-open', 'is-closing');
        this.details.classList.remove('is-closing');
        return;
      }

      this.closing = true;
      this.reopening = true;
      this.details.open = true; // hold it open for the length of the fade
      this.content.classList.remove('is-open');
      this.content.classList.add('is-closing');
      // Also on the <details>: holding `open` on above keeps every [open]-keyed
      // rule live for CLOSE_MS, and the chevron is one of them. Without a marker
      // the caret cannot start unwinding until the panel is already gone.
      this.details.classList.add('is-closing');
      clearTimeout(this.timer);
      this.timer = setTimeout(this.finishClose.bind(this), CLOSE_MS);
    }

    cancelClose() {
      this.closing = false;
      clearTimeout(this.timer);
      this.content.classList.remove('is-closing');
      this.details.classList.remove('is-closing');
      this.content.classList.add('is-open');
    }

    finishClose() {
      this.closing = false;
      this.finishing = true;
      this.content.classList.remove('is-closing', 'is-open');
      this.details.classList.remove('is-closing');
      this.details.open = false;
      var summary = this.details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    }
  }

  if (!customElements.get('pure-mega-motion')) {
    customElements.define('pure-mega-motion', PureMegaMotion);
  }

  /* -------------------------------------------------------------------------
   * <pure-tab-motion> -- crossfade for pure-tabs panels.
   *
   * Drop it inside a <pure-tabs> and it intercepts `pure-tabs:change`, holding
   * the outgoing panel visible long enough to fade it out before the incoming
   * one fades in. pure-tabs still owns aria-selected and `hidden`; this only
   * defers when `hidden` flips, so screen readers and keyboard users are never
   * waiting on the animation.
   * ---------------------------------------------------------------------- */
  class PureTabMotion extends HTMLElement {
    connectedCallback() {
      this.tabsEl = this.closest('pure-tabs');
      if (!this.tabsEl) return;
      this.durationOut = durationFromAttribute(this.dataset.durationOut, TAB_OUT_MS);
      this.durationIn = durationFromAttribute(this.dataset.durationIn, TAB_IN_MS);
      this.onChange = this.handleChange.bind(this);
      this.tabsEl.addEventListener('pure-tabs:change', this.onChange);
    }

    disconnectedCallback() {
      if (this.tabsEl) this.tabsEl.removeEventListener('pure-tabs:change', this.onChange);
      clearTimeout(this.outTimer);
      clearTimeout(this.inTimer);
      if (this.raf) cancelAnimationFrame(this.raf);
      // leave no half-faded panel behind if the section is torn down mid-transition
      if (this.pending) this.pending.forEach(function (p) {
        p.classList.remove('is-leaving', 'is-entering');
      });
    }

    handleChange(event) {
      var detail = event.detail;
      if (!detail || !detail.incoming || prefersReducedMotion()) return;

      detail.defer = true;
      var outgoing = detail.outgoing;
      var incoming = detail.incoming;
      this.pending = [outgoing, incoming].filter(Boolean);

      // A fast click during a transition must not leave stale classes behind.
      clearTimeout(this.outTimer);
      clearTimeout(this.inTimer);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.pending.forEach(function (p) { p.classList.remove('is-leaving', 'is-entering'); });

      var reveal = function () {
        detail.swap();
        incoming.classList.add('is-entering');
        // two frames so the browser paints opacity 0 before we transition off it
        this.raf = requestAnimationFrame(
          function () {
            this.raf = requestAnimationFrame(
              function () {
                incoming.classList.remove('is-entering');
              }.bind(this)
            );
          }.bind(this)
        );
        this.inTimer = setTimeout(function () {
          incoming.classList.remove('is-entering');
        }, this.durationIn);
      }.bind(this);

      if (!outgoing) {
        reveal();
        return;
      }
      outgoing.classList.add('is-leaving');
      this.outTimer = setTimeout(
        function () {
          outgoing.classList.remove('is-leaving');
          reveal();
        },
        this.durationOut
      );
    }
  }

  if (!customElements.get('pure-tab-motion')) {
    customElements.define('pure-tab-motion', PureTabMotion);
  }
})();
